import { computed } from '@angular/core';
import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';

/**
 * Runtime tenant context for the calendar portal.
 *
 * Populated exactly once, by `TenantContextService.initialize()` in the app
 * initializer, from the `/t/:slug` URL prefix (or the legacy `?t=` shim):
 *   - `slug`           — the URL-derived tenant slug, or null on the default
 *                        (AppliedBioX) portal.
 *   - `tenantId`       — resolved by `GET /tenants/by-slug/:slug/branding`;
 *                        null until (and unless) the slug resolves.
 *   - `brandingLoaded` — the branding init settled (successfully themed OR
 *                        fell back to defaults). Features can gate on this
 *                        without caring which way it went.
 */
export interface TenantContextStateI {
  slug: string | null;
  tenantId: string | null;
  brandingLoaded: boolean;
}

const initialTenantContextState: TenantContextStateI = {
  slug: null,
  tenantId: null,
  brandingLoaded: false,
};

export const TenantContextStore = signalStore(
  { providedIn: 'root' },
  withState<TenantContextStateI>(() => ({ ...initialTenantContextState })),
  withComputed(({ slug, tenantId }) => ({
    /** True whenever the portal was opened through a tenant mount. */
    hasTenantContext: computed(() => slug() !== null),
    /** True only when the slug resolved to a real (non-deleted) tenant. */
    isTenantResolved: computed(() => tenantId() !== null),
    /** Router-link prefix preserving the tenant mount ('' on the default portal). */
    routePrefix: computed(() => {
      const s = slug();
      return s ? `/t/${s}` : '';
    }),
  })),
  withMethods(store => ({
    /** URL parse result — set before the branding fetch starts. */
    setSlug(slug: string): void {
      patchState(store, { slug });
    },
    /** By-slug branding resolved: record the tenant identity. */
    setResolvedTenant(tenantId: string): void {
      patchState(store, { tenantId, brandingLoaded: true });
    },
    /** Branding settled without resolving a tenant (unknown slug / fetch
     *  failure → design-system defaults were applied). */
    markBrandingLoaded(): void {
      patchState(store, { brandingLoaded: true });
    },
  })),
);
